import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, RotateCcw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

/** AI Job Monitor — unified view over all background jobs: single & bulk
 * translations, Vault AI analysis and failed AutoSEO imports. Read-only,
 * plus a retry button for failed single translations. */

const STATUS_STYLE = {
  running: "border-sky-400/50 text-sky-300", pending: "border-sky-400/50 text-sky-300",
  done: "border-emerald-400/50 text-emerald-300",
  error: "border-red-400/60 text-red-300", cancelled: "border-amber-400/50 text-amber-300",
};

const Badge = ({ s }) => (
  <span className={`text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 border ${STATUS_STYLE[s] || "border-white/20 text-bone/50"}`}>{s}</span>
);

function Progress({ done, total }) {
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="w-full max-w-[180px]">
      <div className="h-1.5 bg-white/5"><div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} /></div>
      <p className="mt-0.5 text-[10px] font-mono text-bone/40">{done}/{total || "?"}</p>
    </div>
  );
}

export default function AdminAIJobs() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [retrying, setRetrying] = useState("");

  const load = useCallback(async () => {
    try { setData(await adminApi.jobsOverview()); setErr(""); }
    catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  const retryTranslation = async (articleId) => {
    setRetrying(articleId);
    try { await adminApi.translateAsync(articleId); await load(); }
    catch (e) { setErr(e.message); }
    finally { setRetrying(""); }
  };

  const single = data?.translations_single || [];
  const bulk = data?.translations_bulk || [];
  const vault = data?.vault_jobs || [];
  const autoseoFailed = data?.autoseo_failed || [];

  return (
    <AdminShell title="AI Job Monitor">
      <div className="flex flex-wrap items-center gap-3 -mt-6 mb-8">
        <span className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-sky-400/40 text-sky-300" data-testid="jobs-running">
          {data?.running ?? 0} running
        </span>
        <span className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-red-400/40 text-red-300" data-testid="jobs-failed">
          {data?.failed ?? 0} failed
        </span>
        <span className="text-[10px] text-bone/35">auto-refreshes every 5s</span>
        <button onClick={load} data-testid="jobs-refresh"
          className="ml-auto inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Article translations (Claude)</h2>
      <div className="border border-white/10 bg-white/[0.02] mb-8 divide-y divide-white/5" data-testid="jobs-translations">
        {single.length === 0 && <p className="px-4 py-3 text-sm text-bone/40">No translation jobs yet.</p>}
        {single.map((j) => (
          <div key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`job-translation-${j.id}`}>
            {j.status === "running" && <Loader2 size={12} className="animate-spin text-sky-300 shrink-0" />}
            <p className="text-sm text-bone/80 truncate flex-1 min-w-[160px]">{j.slug || j.article_id}</p>
            <Badge s={j.status} />
            <span className="text-[10px] font-mono text-bone/35">{String(j.created_at).slice(5, 16).replace("T", " ")}</span>
            {j.status === "error" && (
              <>
                <span className="text-[11px] text-red-300/80 max-w-[280px] truncate" title={j.error_detail}>{j.error}</span>
                <button onClick={() => retryTranslation(j.article_id)} disabled={retrying === j.article_id}
                  data-testid={`job-retry-${j.id}`}
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-gold/40 px-2.5 py-1 text-gold hover:bg-gold/10 disabled:opacity-40">
                  <RotateCcw size={10} /> Retry
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Bulk translation jobs</h2>
      <div className="border border-white/10 bg-white/[0.02] mb-8 divide-y divide-white/5" data-testid="jobs-bulk">
        {bulk.length === 0 && <p className="px-4 py-3 text-sm text-bone/40">No bulk jobs yet.</p>}
        {bulk.map((j) => (
          <div key={j.id} className="flex flex-wrap items-center gap-4 px-4 py-2.5">
            <p className="text-sm text-bone/80 min-w-[120px]">{j.current_slug || "bulk EN→NL"}</p>
            <Badge s={j.status} />
            <Progress done={j.processed || 0} total={j.total || 0} />
            <span className="text-[10px] font-mono text-bone/35 ml-auto">{j.updated || 0} updated · {j.failed || 0} failed</span>
          </div>
        ))}
      </div>

      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Vault AI analysis (images)</h2>
      <div className="border border-white/10 bg-white/[0.02] mb-8 divide-y divide-white/5" data-testid="jobs-vault">
        {vault.length === 0 && <p className="px-4 py-3 text-sm text-bone/40">No Vault jobs yet.</p>}
        {vault.map((j) => (
          <div key={j.id} className="flex flex-wrap items-center gap-4 px-4 py-2.5">
            <p className="text-sm text-bone/80 min-w-[120px]">{j.kind || "analyze"} · {j.scope || "all"}</p>
            <Badge s={j.status} />
            <Progress done={(j.done || 0) + (j.failed || 0) + (j.skipped || 0)} total={j.total || 0} />
            <span className="text-[10px] font-mono text-bone/35 ml-auto">{j.done || 0} done · {j.failed || 0} failed · {j.skipped || 0} skipped</span>
          </div>
        ))}
      </div>

      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Failed AutoSEO imports</h2>
      <div className="border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid="jobs-autoseo-failed">
        {autoseoFailed.length === 0 && <p className="px-4 py-3 text-sm text-emerald-300/80">No failed imports.</p>}
        {autoseoFailed.map((l, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            <p className="text-sm text-bone/80 truncate flex-1">{l.title || l.slug || l.autoseo_id || "unknown delivery"}</p>
            <span className="text-[11px] text-red-300/80 max-w-[300px] truncate" title={l.error}>{l.error || "—"}</span>
            <span className="text-[10px] font-mono text-bone/35">{String(l.at).slice(5, 16).replace("T", " ")}</span>
          </div>
        ))}
      </div>

      {err && <p className="mt-4 text-sm text-red-400" data-testid="jobs-error">{err}</p>}
    </AdminShell>
  );
}
